import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { formatSeconds, type Message, type Task } from '@machs/shared';
import { useSync } from '../lib/sync';
import { Icon, type IconName } from '../lib/icons';
import { findUrl } from '../lib/format';
import { api } from '../lib/api';
import { mentionIds, usePeople, type Person } from '../lib/people';
import { usePlan } from '../lib/plan';
import { Avatar, Popover, useToast } from './ui';
import { avatarGradient, initials } from '../lib/format';
import { FormBuilderDialog } from './FormBuilder';

export interface ReplyTarget {
  message: Message;
  point: number | null;
}

type Special = 'meilenstein' | 'rueckfrage' | null;

export async function mediaMeta(file: File): Promise<{ width: number | null; height: number | null; durationSec: number | null }> {
  const url = URL.createObjectURL(file);
  try {
    if (file.type.startsWith('image/')) {
      const img = new Image();
      img.src = url;
      await img.decode().catch(() => {});
      return { width: img.naturalWidth || null, height: img.naturalHeight || null, durationSec: null };
    }
    if (file.type.startsWith('video/') || file.type.startsWith('audio/')) {
      const el = document.createElement(file.type.startsWith('video/') ? 'video' : 'audio');
      el.preload = 'metadata';
      el.src = url;
      await new Promise((r) => ((el.onloadedmetadata = r), (el.onerror = r), setTimeout(r, 4000)));
      const v = el as HTMLVideoElement;
      return { width: v.videoWidth || null, height: v.videoHeight || null, durationSec: Number.isFinite(el.duration) ? el.duration : null };
    }
  } finally {
    URL.revokeObjectURL(url);
  }
  return { width: null, height: null, durationSec: null };
}

/** Art der Nachricht zu einer Datei. */
export function kindOf(file: File): 'bild' | 'video' | 'sprache' | 'datei' {
  return file.type.startsWith('image/') ? 'bild' : file.type.startsWith('video/') ? 'video' : file.type.startsWith('audio/') ? 'sprache' : 'datei';
}

interface DraftPreview {
  title: string | null;
  description: string | null;
  site: string;
  image: string | null;
}

export function Composer({ task, reply, onClearReply, editing = null, onClearEdit = () => {} }: {
  task: Task;
  reply: ReplyTarget | null;
  onClearReply: () => void;
  /** Eigene Nachricht, deren Text gerade geändert wird */
  editing?: Message | null;
  onClearEdit?: () => void;
}) {
  const { client, me } = useSync();
  const plan = usePlan();
  const people = usePeople();
  const toast = useToast();
  const [special, setSpecial] = useState<Special>(null);
  const [building, setBuilding] = useState(false);
  const [text, setText] = useState('');
  const [recording, setRecording] = useState<{ since: number } | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const input = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [fileAccept, setFileAccept] = useState('image/*,video/*');

  useEffect(() => {
    if (reply) input.current?.focus();
  }, [reply]);

  // Bearbeiten: der alte Text steht im Feld, Senden speichert ihn
  useEffect(() => {
    if (!editing) return;
    setText(editing.body);
    setSpecial(null);
    requestAnimationFrame(() => {
      const el = input.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    });
  }, [editing]);

  // @-Auswahl: Wort mit @ direkt vor dem Cursor
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const candidates = useMemo(() => {
    if (!mention) return [];
    const q = mention.query.toLowerCase();
    return people
      .filter((p) => p.id !== me.user.id && (!q || p.name.toLowerCase().split(/\s+/).some((w) => w.startsWith(q)) || p.name.toLowerCase().startsWith(q)))
      // Wer an dieser Aufgabe beteiligt ist, steht oben
      .sort((a, b) => Number(b.taskId === task.id) - Number(a.taskId === task.id) || Number(a.external) - Number(b.external) || a.name.localeCompare(b.name))
      .slice(0, 8);
  }, [mention, people, me.user.id, task.id]);

  function trackMention(value: string, caret: number) {
    const m = value.slice(0, caret).match(/(^|\s)@([\p{L}\p{N}._-]{0,30})$/u);
    setMention(m ? { start: caret - m[2]!.length - 1, query: m[2]! } : null);
    setMentionIndex(0);
  }

  function pickMention(p: Person) {
    if (!mention) return;
    const el = input.current;
    const caret = el?.selectionStart ?? text.length;
    const next = `${text.slice(0, mention.start)}@${p.name} ${text.slice(caret)}`;
    setText(next);
    setMention(null);
    const pos = mention.start + p.name.length + 2;
    requestAnimationFrame(() => (el?.focus(), el?.setSelectionRange(pos, pos)));
  }

  /** Über das Menü: @ an der Schreibmarke einfügen und die Auswahl öffnen. */
  function insertAt() {
    const el = input.current;
    const caret = el?.selectionStart ?? text.length;
    const before = text.slice(0, caret);
    const prefix = before && !/\s$/.test(before) ? ' @' : '@';
    const next = `${before}${prefix}${text.slice(caret)}`;
    setText(next);
    const pos = caret + prefix.length;
    setMention({ start: pos - 1, query: '' });
    requestAnimationFrame(() => (el?.focus(), el?.setSelectionRange(pos, pos)));
  }

  // Link-Vorschau schon beim Tippen; mit dem Kreuz geht der Link als reiner Text
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  const [draft, setDraft] = useState<{ url: string; data: DraftPreview | null } | null>(null);
  const draftUrl = !special && !editing ? findUrl(text) : null;
  useEffect(() => {
    if (!draftUrl || dismissed.has(draftUrl)) return setDraft(null);
    if (draft?.url === draftUrl) return;
    let alive = true;
    const t = setTimeout(() => {
      setDraft({ url: draftUrl, data: null });
      api<DraftPreview>(`/media/link-preview?url=${encodeURIComponent(draftUrl)}`)
        .then((data) => alive && setDraft({ url: draftUrl, data }))
        .catch(() => alive && setDraft(null));
    }, 500);
    return () => {
      alive = false;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftUrl, dismissed]);

  useEffect(() => {
    if (!recording) return;
    const t = setInterval(() => setElapsed((Date.now() - recording.since) / 1000), 250);
    return () => clearInterval(t);
  }, [recording]);

  // Textfeld wächst mit
  useEffect(() => {
    const el = input.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [text]);

  function common() {
    return {
      taskId: task.id,
      replyToId: reply?.message.id ?? null,
      replyPoint: reply?.point ?? null,
    };
  }

  function reset() {
    setText('');
    setSpecial(null);
    setMention(null);
    setDraft(null);
    onClearReply();
    if (editing) onClearEdit();
  }

  async function send() {
    const body = text.trim();
    if (!body) return;
    const mentions = mentionIds(body, people);
    if (editing) {
      await client.updateMessage(editing.id, { body, mentions });
      reset();
      return;
    }
    const found = special ? null : findUrl(body);
    // Weggeklickte Vorschau: der Link bleibt reiner Text
    const url = found && !dismissed.has(found) ? found : null;
    await client.sendMessage({
      ...common(),
      kind: special ?? (url ? 'link' : 'text'),
      body,
      mentions,
      upload: url ? { kind: 'link', url, file: null, fileName: null, mime: null, durationSec: null, width: null, height: null } : undefined,
    });
    reset();
  }

  /** Eigenen Standort als Karte senden (OpenStreetMap-Link, Vorschau macht der Server). */
  function sendLocation() {
    if (!navigator.geolocation) return toast('Standort ist in diesem Browser nicht verfügbar');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude.toFixed(5);
        const lon = pos.coords.longitude.toFixed(5);
        const url = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=17/${lat}/${lon}`;
        void client.sendMessage({
          ...common(),
          kind: 'link',
          body: url,
          upload: { kind: 'link', url, file: null, fileName: null, mime: null, durationSec: null, width: null, height: null },
        });
        onClearReply();
      },
      () => toast('Standort nicht freigegeben'),
      { enableHighAccuracy: true, timeout: 15_000 },
    );
  }

  async function sendFile(file: File) {
    const kind = kindOf(file);
    const meta = await mediaMeta(file);
    if (!plan.allowUpload(file.size, kind === 'video' ? meta.durationSec : null)) return;
    await client.sendMessage({
      ...common(),
      kind,
      body: text.trim(),
      upload: { kind, file, fileName: file.name, mime: file.type || null, url: null, ...meta },
    });
    reset();
  }

  // Sprachnachricht: Klick schaltet die Aufnahme um, Gedrückthalten nimmt bis zum Loslassen auf
  const starting = useRef<Promise<boolean> | null>(null);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const held = useRef(false);
  const [holding, setHolding] = useState(false);

  async function startRecording(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const type = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/webm'].find((t) => MediaRecorder.isTypeSupported(t));
      const rec = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
      chunks.current = [];
      const since = Date.now();
      rec.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(null);
        setElapsed(0);
        const durationSec = (Date.now() - since) / 1000;
        if (!plan.allowUpload(chunks.current.reduce((n, c) => n + c.size, 0))) return;
        if (durationSec < 0.8) return toast('Zu kurz, zum Aufnehmen klicken oder gedrückt halten');
        const mime = rec.mimeType || 'audio/webm';
        const blob = new Blob(chunks.current, { type: mime });
        await client.sendMessage({
          ...common(),
          kind: 'sprache',
          body: text.trim(),
          upload: { kind: 'sprache', file: blob, fileName: `sprachnachricht.${mime.includes('mp4') ? 'm4a' : 'webm'}`, mime, url: null, durationSec, width: null, height: null },
        });
        reset();
      };
      rec.start(1000);
      recorder.current = rec;
      setRecording({ since });
      return true;
    } catch {
      toast('Kein Zugriff auf das Mikrofon');
      return false;
    }
  }

  function toggleRecording() {
    if (recording) recorder.current?.stop();
    else void startRecording();
  }

  function micDown() {
    if (recording) return;
    held.current = false;
    holdTimer.current = setTimeout(() => {
      held.current = true;
      setHolding(true);
      starting.current = startRecording();
    }, 250);
  }

  async function micUp() {
    if (holdTimer.current) clearTimeout(holdTimer.current);
    holdTimer.current = null;
    if (!starting.current) return;
    const ok = await starting.current;
    starting.current = null;
    setHolding(false);
    if (ok) recorder.current?.stop();
  }

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (mention && candidates.length) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex((i) => (i + (e.key === 'ArrowDown' ? 1 : candidates.length - 1)) % candidates.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        pickMention(candidates[mentionIndex] ?? candidates[0]!);
        return;
      }
    }
    if (e.key === 'Escape') {
      if (mention) return setMention(null);
      if (editing) return reset();
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void send();
    }
  }

  const replyPoint = reply?.point != null ? client.data.media.get(reply.message.mediaId ?? '')?.points[reply.point] : null;

  return (
    <div className="composer">
      {reply && (
        <div className="quote mb-2 flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <b className="block text-[var(--text)]">Antwort an {reply.message.authorName}</b>
            <span className="line-clamp-1">{replyPoint ? `${formatSeconds(replyPoint.at)} ${replyPoint.text}` : reply.message.body || 'Medium'}</span>
          </div>
          <button onClick={onClearReply} aria-label="Antwort verwerfen"><Icon name="x" size={16} /></button>
        </div>
      )}

      {editing && (
        <div className="quote mb-2 flex items-start gap-2">
          <Icon name="edit" size={16} className="mt-0.5 flex-none text-[var(--brand-a)]" />
          <div className="min-w-0 flex-1">
            <b className="block text-[var(--text)]">Nachricht bearbeiten</b>
            <span className="line-clamp-1">{editing.body}</span>
          </div>
          <button onClick={reset} aria-label="Bearbeiten abbrechen"><Icon name="x" size={16} /></button>
        </div>
      )}

      {draft && (
        <div className="draftlink mb-2">
          {draft.data?.image && <img src={draft.data.image} alt="" />}
          <div className="min-w-0 flex-1">
            {draft.data ? (
              <>
                <b className="line-clamp-1 text-[13px]">{draft.data.title ?? draft.url}</b>
                {draft.data.description && <span className="line-clamp-2 text-[12px] text-[var(--muted)]">{draft.data.description}</span>}
                <small className="flex items-center gap-1 text-[11px] text-[var(--muted)]"><Icon name="link" size={11} />{draft.data.site}</small>
              </>
            ) : (
              <span className="text-[12.5px] text-[var(--muted)]">Vorschau wird geladen …</span>
            )}
          </div>
          <button onClick={() => (setDismissed((s) => new Set(s).add(draft.url)), setDraft(null))} aria-label="Ohne Vorschau senden" data-tip="Ohne Vorschau senden">
            <Icon name="x" size={16} />
          </button>
        </div>
      )}

      {special && (
        <span className="role mb-2" style={{ color: special === 'meilenstein' ? 'var(--milestone)' : 'var(--brand-a)', background: 'var(--surface2)' }}>
          <Icon name={special === 'meilenstein' ? 'flag' : 'question'} size={13} />
          {special === 'meilenstein' ? 'Meilenstein' : 'Rückfrage'}
          <button className="ml-1" onClick={() => setSpecial(null)} aria-label="Zurücksetzen"><Icon name="x" size={12} /></button>
        </span>
      )}

      <div className="flex items-end gap-2">
        <Popover up width={330} trigger={({ toggle }) => (
          <button className="roundbtn plain" onClick={toggle} aria-label="Anhängen und mehr"><Icon name="plus" size={22} /></button>
        )}>
          {(close) => {
            const pickFile = (accept: string) => { setFileAccept(accept); close(); setTimeout(() => fileInput.current?.click()); };
            return (
              <>
                <div className="add-grid">
                  <button onClick={() => pickFile('image/*,video/*')}><span style={{ color: 'var(--brand-a)' }}><Icon name="image" size={22} /></span>Foto/Video</button>
                  <button onClick={() => pickFile('*/*')}><span style={{ color: 'var(--pruefung)' }}><Icon name="file" size={22} /></span>Datei</button>
                  <button onClick={() => (setBuilding(true), close())}><span style={{ color: 'var(--brand-a)' }}><Icon name="question" size={22} /></span>Rückfrage</button>
                  <button onClick={() => (setSpecial('meilenstein'), close(), input.current?.focus())}><span style={{ color: 'var(--milestone)' }}><Icon name="flag" size={22} /></span>Meilenstein</button>
                  <button onClick={() => (close(), sendLocation())}><span style={{ color: 'var(--loesung)' }}><Icon name="place" size={22} /></span>Standort</button>
                  <button onClick={() => (close(), insertAt())}><span style={{ color: 'var(--brand-b)' }}><Icon name="at" size={22} /></span>Erwähnen</button>
                </div>
                <p className="m-0 px-2 pb-1 pt-2 text-center text-[12px] text-[var(--muted)]">Tipp: Mikrofon gedrückt halten, loslassen sendet</p>
              </>
            );
          }}
        </Popover>
        <div className="inbox">
          {mention && candidates.length > 0 && (
            <div className="mentionlist" role="listbox" aria-label="Person erwähnen">
              {candidates.map((p, i) => (
                <button
                  key={p.id}
                  role="option"
                  aria-selected={i === mentionIndex}
                  onMouseDown={(e) => (e.preventDefault(), pickMention(p))}
                  onMouseEnter={() => setMentionIndex(i)}
                >
                  <Avatar name={initials(p.name)} gradient={avatarGradient(p.id)} size={26} />
                  <span className="min-w-0 flex-1 truncate">{p.name}</span>
                  {p.external && <small className="rounded-md bg-[var(--surface2)] px-1.5 text-[10.5px] text-[var(--muted)]">extern</small>}
                </button>
              ))}
            </div>
          )}
          {recording ? (
            <div className="flex flex-1 items-center gap-2 py-2.5 text-[var(--problem)]">
              <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-[var(--problem)]" />
              {formatSeconds(elapsed)}
              <span className="text-[13px] text-[var(--muted)]">{holding ? 'Loslassen zum Senden' : 'Zum Senden erneut klicken'}</span>
            </div>
          ) : (
            <textarea
              ref={input}
              rows={1}
              value={text}
              onChange={(e) => (setText(e.target.value), trackMention(e.target.value, e.target.selectionStart))}
              onClick={(e) => trackMention(e.currentTarget.value, e.currentTarget.selectionStart)}
              onBlur={() => setTimeout(() => setMention(null), 150)}
              onKeyDown={onKey}
              onPaste={(e) => {
                const file = [...e.clipboardData.files][0];
                if (file) {
                  e.preventDefault();
                  void sendFile(file);
                }
              }}
              placeholder={editing ? 'Neuer Text' : special === 'meilenstein' ? 'Was wurde erreicht?' : special === 'rueckfrage' ? 'Deine Frage' : 'Nachricht'}
              aria-label="Nachricht"
            />
          )}
          <input
            ref={fileInput}
            type="file"
            hidden
            accept={fileAccept}
            capture={undefined}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void sendFile(f);
            }}
          />
        </div>
        {text.trim() && !recording ? (
          <button className="roundbtn" onClick={() => void send()} aria-label={editing ? 'Änderung speichern' : 'Senden'}><Icon name={editing ? 'check' : 'send'} size={20} /></button>
        ) : (
          <button
            className={`roundbtn ${recording ? 'rec' : ''}`}
            onPointerDown={micDown}
            onPointerUp={() => void micUp()}
            onPointerLeave={() => void micUp()}
            onContextMenu={(e) => e.preventDefault()}
            onClick={() => (held.current ? (held.current = false) : toggleRecording())} aria-label={recording ? 'Aufnahme beenden und senden' : 'Sprachnachricht aufnehmen'}>
            <Icon name={recording ? 'stop' : 'mic'} size={20} />
          </button>
        )}
      </div>
      {building && <FormBuilderDialog taskId={task.id} onClose={() => setBuilding(false)} />}
    </div>
  );
}
